/**
 * @file lib/config/sdk-config-manager.ts
 * @description SDK起動時のconfigファイル管理
 * @author GFTD Co., Ltd.
 */

export interface SDKConfig {
  version: string
  initialized: boolean
  defaultActorId?: string
  actors: {
    [key: string]: {
      id: string
      name: string
      type: string
      repositoryType: 'local' | 'online'
      repositoryPath: string
      repositoryUrl?: string
      gitConfig?: any
      lastAccessed: string
      isDefault?: boolean
    }
  }
  settings: {
    theme: 'light' | 'dark'
    autoSave: boolean
    showWelcome: boolean
    defaultView: 'actors' | 'code' | 'preview'
  }
  created: string
  lastModified: string
}

/**
 * デフォルトアクター設定テンプレート
 */
export interface DefaultActorTemplate {
  name: string
  description: string
  type: string
  repositoryType: 'local'
  gitConfig: {
    user: {
      name: string
      email: string
    }
    remoteUrl?: string
    defaultBranch: string
    autoCommit: boolean
    autoSync: boolean
  }
}

/**
 * 利用可能なデフォルトアクタータイプ
 */
export const DEFAULT_ACTOR_TEMPLATES: Record<string, DefaultActorTemplate> = {
  'chat-assistant': {
    name: 'Chat Assistant',
    description: 'AI-powered chat assistant for conversations and Q&A',
    type: 'chat-assistant',
    repositoryType: 'local',
    gitConfig: {
      user: {
        name: 'Actor SDK User',
        email: 'user@actor-sdk.local'
      },
      defaultBranch: 'main',
      autoCommit: true,
      autoSync: false
    }
  },
  'code-generator': {
    name: 'Code Generator',
    description: 'AI assistant specialized in code generation and programming help',
    type: 'code-generator', 
    repositoryType: 'local',
    gitConfig: {
      user: {
        name: 'Actor SDK User',
        email: 'user@actor-sdk.local'
      },
      defaultBranch: 'main',
      autoCommit: true,
      autoSync: false
    }
  },
  'document-processor': {
    name: 'Document Processor',
    description: 'AI assistant for document analysis and processing',
    type: 'document-processor',
    repositoryType: 'local',
    gitConfig: {
      user: {
        name: 'Actor SDK User',
        email: 'user@actor-sdk.local'
      },
      defaultBranch: 'main',
      autoCommit: true,
      autoSync: false
    }
  }
}

/**
 * デフォルトのSDK設定
 */
export function createDefaultSDKConfig(): SDKConfig {
  return {
    version: '1.0.0',
    initialized: false,
    actors: {},
    settings: {
      theme: 'light',
      autoSave: true,
      showWelcome: true,
      defaultView: 'actors'
    },
    created: new Date().toISOString(),
    lastModified: new Date().toISOString()
  }
}

/**
 * SDK設定管理クラス
 */
export class SDKConfigManager {
  private static readonly CONFIG_FILE_PATH = '/sdk.config.json'
  private static readonly LOCAL_STORAGE_KEY = 'actor-sdk-config'

  /**
   * 実行環境がブラウザかチェック
   */
  private static isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined'
  }

  /**
   * 実行環境がNode.jsかチェック
   */
  private static isNode(): boolean {
    return typeof process !== 'undefined' && 
           process.versions !== undefined && 
           process.versions.node !== undefined &&
           typeof window === 'undefined'
  }

  /**
   * configファイルが存在するかチェック
   */
  static async hasConfig(): Promise<boolean> {
    try {
      // ブラウザ環境では localStorage を使用
      if (this.isBrowser()) {
        const config = localStorage.getItem(this.LOCAL_STORAGE_KEY)
        return config !== null
      }

      // Node.js環境では動的にfsを読み込み（サーバーサイドのみ）
      if (this.isNode()) {
        try {
          const fs = await import('fs')
          return fs.existsSync(this.CONFIG_FILE_PATH)
        } catch (fsError) {
          console.warn('fs module not available:', fsError)
          return false
        }
      }

      // どちらでもない場合（Edge case）
      console.warn('Unknown environment, using fallback behavior')
      return false
    } catch (error) {
      console.error('Failed to check config existence:', error)
      return false
    }
  }

  /**
   * configファイルを読み込み
   */
  static async loadConfig(): Promise<SDKConfig | null> {
    try {
      // ブラウザ環境
      if (this.isBrowser()) {
        const configStr = localStorage.getItem(this.LOCAL_STORAGE_KEY)
        if (configStr) {
          const config = JSON.parse(configStr)
          return this.validateAndMigrateConfig(config)
        }
        return null
      }

      // Node.js環境
      if (this.isNode()) {
        try {
          const fs = await import('fs')
          if (fs.existsSync(this.CONFIG_FILE_PATH)) {
            const configStr = fs.readFileSync(this.CONFIG_FILE_PATH, 'utf8')
            const config = JSON.parse(configStr)
            return this.validateAndMigrateConfig(config)
          }
        } catch (fsError) {
          console.warn('fs module not available:', fsError)
          return null
        }
      }

      return null
    } catch (error) {
      console.error('Failed to load config:', error)
      return null
    }
  }

  /**
   * configファイルを保存
   */
  static async saveConfig(config: SDKConfig): Promise<boolean> {
    try {
      config.lastModified = new Date().toISOString()
      const configStr = JSON.stringify(config, null, 2)

      // ブラウザ環境
      if (this.isBrowser()) {
        localStorage.setItem(this.LOCAL_STORAGE_KEY, configStr)
        return true
      }

      // Node.js環境
      if (this.isNode()) {
        try {
          const fs = await import('fs')
          fs.writeFileSync(this.CONFIG_FILE_PATH, configStr)
          return true
        } catch (fsError) {
          console.warn('fs module not available:', fsError)
          return false
        }
      }

      return false
    } catch (error) {
      console.error('Failed to save config:', error)
      return false
    }
  }

  /**
   * configにアクターを追加
   */
  static async addActor(actorData: {
    id: string
    name: string
    type: string
    repositoryType: 'local' | 'online'
    repositoryPath: string
    repositoryUrl?: string
    gitConfig?: any
  }): Promise<boolean> {
    try {
      let config = await this.loadConfig()
      if (!config) {
        config = createDefaultSDKConfig()
      }

      config.actors[actorData.id] = {
        ...actorData,
        lastAccessed: new Date().toISOString(),
        isDefault: Object.keys(config.actors).length === 0 // 最初のアクターをデフォルトに
      }

      if (!config.defaultActorId && Object.keys(config.actors).length === 1) {
        config.defaultActorId = actorData.id
      }

      config.initialized = true
      return await this.saveConfig(config)
    } catch (error) {
      console.error('Failed to add actor to config:', error)
      return false
    }
  }

  /**
   * デフォルトアクターを設定
   */
  static async setDefaultActor(actorId: string): Promise<boolean> {
    try {
      const config = await this.loadConfig()
      if (!config || !config.actors[actorId]) {
        return false
      }

      // 既存のデフォルトを解除
      Object.values(config.actors).forEach(actor => {
        actor.isDefault = false
      })

      // 新しいデフォルトを設定
      config.actors[actorId].isDefault = true
      config.defaultActorId = actorId

      return await this.saveConfig(config)
    } catch (error) {
      console.error('Failed to set default actor:', error)
      return false
    }
  }

  /**
   * アクターのアクセス時刻を更新
   */
  static async updateActorAccess(actorId: string): Promise<boolean> {
    try {
      const config = await this.loadConfig()
      if (!config || !config.actors[actorId]) {
        return false
      }

      config.actors[actorId].lastAccessed = new Date().toISOString()
      return await this.saveConfig(config)
    } catch (error) {
      console.error('Failed to update actor access:', error)
      return false
    }
  }

  /**
   * 設定を更新
   */
  static async updateSettings(settings: Partial<SDKConfig['settings']>): Promise<boolean> {
    try {
      let config = await this.loadConfig()
      if (!config) {
        config = createDefaultSDKConfig()
      }

      config.settings = { ...config.settings, ...settings }
      return await this.saveConfig(config)
    } catch (error) {
      console.error('Failed to update settings:', error)
      return false
    }
  }

  /**
   * configを初期化済みにマーク
   */
  static async markAsInitialized(): Promise<boolean> {
    try {
      let config = await this.loadConfig()
      if (!config) {
        config = createDefaultSDKConfig()
      }

      config.initialized = true
      config.settings.showWelcome = false
      return await this.saveConfig(config)
    } catch (error) {
      console.error('Failed to mark as initialized:', error)
      return false
    }
  }

  /**
   * configを検証・マイグレーション
   */
  private static validateAndMigrateConfig(config: any): SDKConfig {
    const defaultConfig = createDefaultSDKConfig()

    // 基本的な検証とデフォルト値の補完
    const validatedConfig: SDKConfig = {
      version: config.version || defaultConfig.version,
      initialized: Boolean(config.initialized),
      defaultActorId: config.defaultActorId,
      actors: config.actors || {},
      settings: {
        theme: config.settings?.theme || defaultConfig.settings.theme,
        autoSave: config.settings?.autoSave !== undefined ? config.settings.autoSave : defaultConfig.settings.autoSave,
        showWelcome: config.settings?.showWelcome !== undefined ? config.settings.showWelcome : defaultConfig.settings.showWelcome,
        defaultView: config.settings?.defaultView || defaultConfig.settings.defaultView
      },
      created: config.created || defaultConfig.created,
      lastModified: config.lastModified || defaultConfig.lastModified
    }

    return validatedConfig
  }

  /**
   * configをリセット（開発用）
   */
  static async resetConfig(): Promise<boolean> {
    try {
      // ブラウザ環境
      if (this.isBrowser()) {
        localStorage.removeItem(this.LOCAL_STORAGE_KEY)
        return true
      }

      // Node.js環境
      if (this.isNode()) {
        try {
          const fs = await import('fs')
          if (fs.existsSync(this.CONFIG_FILE_PATH)) {
            fs.unlinkSync(this.CONFIG_FILE_PATH)
          }
          return true
        } catch (fsError) {
          console.warn('fs module not available:', fsError)
          return false
        }
      }

      return false
    } catch (error) {
      console.error('Failed to reset config:', error)
      return false
    }
  }

  /**
   * デバッグ情報を出力（ブラウザのコンソールに表示）
   */
  static async debugConfig(): Promise<void> {
    try {
      const config = await this.loadConfig()
      console.group('🔧 Actor SDK Configuration Debug')
      console.log('Config exists:', config !== null)
      console.log('Environment:', this.isBrowser() ? 'Browser' : this.isNode() ? 'Node.js' : 'Unknown')
      console.log('Storage method:', this.isBrowser() ? 'localStorage' : 'File System')
      
      if (config) {
        console.log('Configuration:', config)
        console.log('Actors count:', Object.keys(config.actors).length)
        console.log('Default actor:', config.defaultActorId)
        console.log('Initialized:', config.initialized)
      } else {
        console.log('No configuration found')
      }
      console.groupEnd()
    } catch (error) {
      console.error('Failed to debug config:', error)
    }
  }

  /**
   * デフォルトアクターを作成
   */
  static async createDefaultActor(template: keyof typeof DEFAULT_ACTOR_TEMPLATES = 'chat-assistant'): Promise<{ success: boolean; actorId?: string; error?: string }> {
    try {
      const actorTemplate = DEFAULT_ACTOR_TEMPLATES[template]
      if (!actorTemplate) {
        return { success: false, error: `Unknown template: ${template}` }
      }

      // UUIDライクなIDを生成
      const actorId = `${template}-${Math.random().toString(36).substring(2, 15)}`
      const timestamp = new Date().toISOString()

      // リポジトリパスを生成
      const repositoryPath = `/actors/${actorId}`

      // configを読み込みまたは作成
      let config = await this.loadConfig()
      if (!config) {
        config = createDefaultSDKConfig()
      }

      // デフォルトアクターを追加
      config.actors[actorId] = {
        id: actorId,
        name: actorTemplate.name,
        type: actorTemplate.type,
        repositoryType: actorTemplate.repositoryType,
        repositoryPath: repositoryPath,
        gitConfig: actorTemplate.gitConfig,
        lastAccessed: timestamp,
        isDefault: true
      }

      // デフォルトアクターとして設定
      config.defaultActorId = actorId
      config.lastModified = timestamp

      // 設定を保存
      const saved = await this.saveConfig(config)
      if (!saved) {
        return { success: false, error: 'Failed to save configuration' }
      }

      console.log(`✅ Default actor created: ${actorTemplate.name} (${actorId})`)
      return { success: true, actorId }
    } catch (error) {
      console.error('Failed to create default actor:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  }

  /**
   * 初期セットアップ - デフォルトアクターと設定を作成
   */
  static async performInitialSetup(actorTemplate: keyof typeof DEFAULT_ACTOR_TEMPLATES = 'chat-assistant'): Promise<{ success: boolean; config?: SDKConfig; error?: string }> {
    try {
      console.log('🚀 Starting initial Actor SDK setup...')

      // デフォルトアクターを作成
      const actorResult = await this.createDefaultActor(actorTemplate)
      if (!actorResult.success) {
        return { success: false, error: actorResult.error }
      }

      // 初期化完了をマーク
      const marked = await this.markAsInitialized()
      if (!marked) {
        return { success: false, error: 'Failed to mark as initialized' }
      }

      // 最新の設定を取得
      const config = await this.loadConfig()
      if (!config) {
        return { success: false, error: 'Failed to load configuration after setup' }
      }

      console.log('✅ Initial setup completed successfully!')
      return { success: true, config }
    } catch (error) {
      console.error('Failed to perform initial setup:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  }

  /**
   * 利用可能なデフォルトアクタータイプのリストを取得
   */
  static getAvailableTemplates(): Array<{ key: string; template: DefaultActorTemplate }> {
    return Object.entries(DEFAULT_ACTOR_TEMPLATES).map(([key, template]) => ({
      key,
      template
    }))
  }
} 